import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Cloud,
  Clock,
  RefreshCw,
  Upload,
  X,
  Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Connector, ConnectorImportMode } from '../services/api';
import type { LockedFeatureInfo } from '../navigation/featureAccess';
import type { IngestionHistoryItem } from '../utils/ingestionHistory';
import CustomSelect, { SelectOption } from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './AddVideoSidebar.css';

export type { IngestionHistoryItem } from '../utils/ingestionHistory';

export type AddVideoMode = 'upload' | 'import';

interface AddVideoSidebarProps {
  isOpen: boolean;
  indexName: string;
  selectedIndexId: string | null;
  mode: AddVideoMode;
  onModeChange: (mode: AddVideoMode) => void;
  onClose: () => void;
  connectors: Connector[];
  isLoadingConnectors: boolean;
  selectedConnectorId: string;
  onSelectedConnectorIdChange: (value: string) => void;
  sourcePrefix: string;
  onSourcePrefixChange: (value: string) => void;
  filePattern: string;
  onFilePatternChange: (value: string) => void;
  recursive: boolean;
  onRecursiveChange: (value: boolean) => void;
  importModeOverride: '' | ConnectorImportMode;
  onImportModeOverrideChange: (value: '' | ConnectorImportMode) => void;
  isCreatingImportJob: boolean;
  onImport: () => void;
  onLoadConnectors: () => void;
  importLockedFeature?: LockedFeatureInfo | null;
  historyItems: IngestionHistoryItem[];
  historyLastSeenAt: string | null;
  isRefreshingHistory: boolean;
  onRefreshHistory: () => void;
}

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  return date.toLocaleString();
};

const toMs = (value?: string): number => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const isFailedStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === 'failed' || normalized === 'cancelled';
};

const isSuccessStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === 'success' || normalized === 'completed';
};

const isInProgressStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === 'uploading' || normalized === 'starting' || normalized === 'pending' || normalized === 'scanning' || normalized === 'importing';
};

const AddVideoSidebar: React.FC<AddVideoSidebarProps> = ({
  isOpen,
  indexName,
  selectedIndexId,
  mode,
  onModeChange,
  onClose,
  connectors,
  isLoadingConnectors,
  selectedConnectorId,
  onSelectedConnectorIdChange,
  sourcePrefix,
  onSourcePrefixChange,
  filePattern,
  onFilePatternChange,
  recursive,
  onRecursiveChange,
  importModeOverride,
  onImportModeOverrideChange,
  isCreatingImportJob,
  onImport,
  onLoadConnectors,
  importLockedFeature,
  historyItems,
  historyLastSeenAt,
  isRefreshingHistory,
  onRefreshHistory,
}) => {
  const navigate = useNavigate();
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('addVideoSidebarWidth');
    return saved ? parseInt(saved, 10) : 820;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  const visibleHistory = useMemo(() => {
    const base = historyItems.filter((item) => {
      if (selectedIndexId && item.index_id !== selectedIndexId) return false;
      return item.type === mode;
    });

    return [...base].sort((a, b) => toMs(b.updated_at || b.created_at) - toMs(a.updated_at || a.created_at));
  }, [historyItems, mode, selectedIndexId]);

  const isNewHistoryItem = (item: IngestionHistoryItem): boolean => {
    if (!historyLastSeenAt) return true;
    const itemTime = toMs(item.created_at);
    const lastSeenTime = toMs(historyLastSeenAt);
    if (!Number.isFinite(itemTime) || !Number.isFinite(lastSeenTime)) return false;
    return itemTime > lastSeenTime;
  };

  const connectorOptions = useMemo<SelectOption[]>(() => ([
    { value: '', label: 'Select a connector...' },
    ...connectors.map((connector) => ({
      value: connector.connector_id,
      label: `${connector.name} (${connector.provider.toUpperCase()} - ${connector.bucket})`,
    })),
  ]), [connectors]);

  const importModeOptions = useMemo<SelectOption[]>(() => ([
    { value: '', label: 'Use connector default' },
    { value: 'new_only', label: 'New files only (dedupe)' },
    { value: 'all', label: 'All matching files' },
  ]), []);

  useEffect(() => {
    localStorage.setItem('addVideoSidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('addVideoSidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-add-video-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(520, Math.min(1400, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.body.classList.remove('resizing-add-video-sidebar');
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-add-video-sidebar');
    };
  }, [isActive, isOpen, isResizing]);

  useEffect(() => {
    if (!isOpen || !isActive) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isActive, isOpen, onClose]);

  if (!isOpen) return null;
  const isImportLocked = !!importLockedFeature;

  return (
    <div className={`add-video-sidebar ${isOpen ? 'sidebar-open' : ''} ${isResizing ? 'resizing' : ''}`} style={{ width: `${sidebarWidth}px` }}>
      <div className={`resize-handle ${isResizing ? 'resizing' : ''}`} onMouseDown={handleResizeStart} />

      <div className="sidebar-header">
        <div className="header-title">
          <h2>Add Videos</h2>
          <p className="header-subtitle">Index: {indexName}</p>
        </div>
        <button className="close-button" onClick={onClose} aria-label="Close add videos">
          <X size={20} />
        </button>
      </div>

      <div className="sidebar-content">
        <div className="add-video-mode-tabs" role="tablist" aria-label="Add video mode">
          <button
            className={`add-video-mode-tab ${mode === 'upload' ? 'active' : ''}`}
            onClick={() => onModeChange('upload')}
            type="button"
            role="tab"
            aria-selected={mode === 'upload'}
          >
            <Upload size={15} />
            Upload
          </button>
          <button
            className={`add-video-mode-tab ${mode === 'import' ? 'active' : ''} ${isImportLocked ? 'locked' : ''}`}
            onClick={() => onModeChange('import')}
            type="button"
            title={importLockedFeature?.reason}
            role="tab"
            aria-selected={mode === 'import'}
          >
            {isImportLocked ? <Lock size={15} /> : <Cloud size={15} />}
            Import
          </button>
        </div>

        {mode === 'import' && isImportLocked && importLockedFeature && (
          <section className="sidebar-section">
            <div className="locked-feature-card compact" role="status" aria-live="polite">
              <div className="locked-feature-card-icon" aria-hidden="true">
                <Lock size={18} />
              </div>
              <div className="locked-feature-card-copy">
                <span className="locked-feature-card-eyebrow">Starter plan required</span>
                <h4>Cloud import</h4>
                <p>{importLockedFeature.reason} {importLockedFeature.freePlanMessage}</p>
              </div>
              <button
                type="button"
                className="locked-feature-card-action"
                onClick={() => navigate(importLockedFeature.upgradePath)}
              >
                View plans
              </button>
            </div>
          </section>
        )}

        {mode === 'import' && !isImportLocked && (
          <section className="sidebar-section">
            <div className="section-header-row">
              <h3>Import From Cloud</h3>
              <button className="ghost-btn" onClick={onLoadConnectors} type="button" disabled={isLoadingConnectors}>
                {isLoadingConnectors ? <RefreshCw size={14} className="spinning" /> : <RefreshCw size={14} />}
                Refresh
              </button>
            </div>

            <div className="field-group">
              <label>Connector</label>
              {isLoadingConnectors ? (
                <div className="inline-loading"><RefreshCw size={14} className="spinning" /> Loading connectors...</div>
              ) : connectors.length === 0 ? (
                <div className="empty-hint">No active connectors available.</div>
              ) : (
                <CustomSelect
                  className="add-video-custom-select"
                  value={selectedConnectorId}
                  onChange={onSelectedConnectorIdChange}
                  options={connectorOptions}
                  placeholder="Select a connector..."
                />
              )}
            </div>

            <div className="field-group">
              <label>Source Path (optional)</label>
              <input
                className="field-input"
                value={sourcePrefix}
                onChange={(e) => onSourcePrefixChange(e.target.value)}
                placeholder="e.g. videos/2024/"
              />
            </div>

            <div className="field-group">
              <label>File Pattern</label>
              <input
                className="field-input"
                value={filePattern}
                onChange={(e) => onFilePatternChange(e.target.value)}
                placeholder="*"
              />
            </div>

            <label className="checkbox-row">
              <input type="checkbox" checked={recursive} onChange={(e) => onRecursiveChange(e.target.checked)} />
              <span>Include subdirectories</span>
            </label>

            <div className="field-group">
              <label>Import Mode Override</label>
              <CustomSelect
                className="add-video-custom-select"
                value={importModeOverride}
                onChange={(value) => onImportModeOverrideChange(value as '' | ConnectorImportMode)}
                options={importModeOptions}
              />
              <div className="field-hint">
                Optional per-job override for duplicate handling.
              </div>
            </div>

            <div className="section-actions">
              <button
                className="primary-btn"
                type="button"
                onClick={onImport}
                disabled={!selectedConnectorId || isCreatingImportJob}
              >
                {isCreatingImportJob ? (
                  <>
                    <RefreshCw size={14} className="spinning" />
                    Starting Import...
                  </>
                ) : (
                  <>
                    <Cloud size={14} />
                    Start Import
                  </>
                )}
              </button>
            </div>
          </section>
        )}

        {mode === 'import' && !isImportLocked && (
          <section className="sidebar-section history-section">
            <div className="section-header-row">
              <h3>Import History</h3>
              <button className="ghost-btn" onClick={onRefreshHistory} type="button" disabled={isRefreshingHistory}>
                {isRefreshingHistory ? <RefreshCw size={14} className="spinning" /> : <RefreshCw size={14} />}
                Refresh
              </button>
            </div>

            <div className="history-list">
              {visibleHistory.length === 0 ? (
                <div className="empty-hint">No import jobs yet.</div>
              ) : (
                visibleHistory.map((item) => {
                  const isNew = isNewHistoryItem(item);
                  const failed = isFailedStatus(item.status);
                  const success = isSuccessStatus(item.status);
                  const inProgress = isInProgressStatus(item.status);
                  return (
                    <div key={item.id} className={`history-item ${isNew ? 'new' : 'old'}`}>
                      <div className="history-left">
                        <div className="history-status-icon">
                          {failed && <AlertCircle size={14} className="failed" />}
                          {success && <CheckCircle size={14} className="success" />}
                          {inProgress && <RefreshCw size={14} className="spinning warning" />}
                          {!failed && !success && !inProgress && <Clock size={14} className="pending" />}
                        </div>
                      </div>

                      <div className="history-main">
                        <div className="history-title-row">
                          <span className="history-title">
                            {item.connector_name || item.connector_id || item.job_id || 'Import job'}
                          </span>
                          <div className="history-badges">
                            {isNew && <span className="new-badge">NEW</span>}
                            <span className={`status-chip ${item.status.toLowerCase()}`}>{item.status}</span>
                          </div>
                        </div>

                        <div className="history-meta">
                          <span>{item.source_prefix || '/'}</span>
                          <span>{item.file_pattern || '*'}</span>
                          {item.import_mode_override && (
                            <span>{item.import_mode_override === 'new_only' ? 'new_only' : 'all'}</span>
                          )}
                          <span>{formatDateTime(item.created_at)}</span>
                        </div>

                        {item.progress && (
                          <div className="history-progress">
                            <span>{item.progress.imported} imported</span>
                            <span>{item.progress.failed} failed</span>
                            <span>{item.progress.skipped} skipped</span>
                          </div>
                        )}

                        {item.error_message && <div className="attempt-error">{item.error_message}</div>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AddVideoSidebar;
